import React, { useState, useRef } from 'react';
import {
	Box,
	Button,
	FormControl,
	InputLabel,
	Select,
	Typography,
	MenuItem,
	Paper,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import {
	createShiftRequest,
	getShiftRequestTypes,
	updateShiftRequest,
} from 'core/API/shift-requests';
import {
	ShiftRequest,
	CreateShiftRequestPayload,
	ShiftRequestType,
	UpdateShiftRequestInput,
} from 'types/shift-requests';
import {
	cacheShiftRequestTypes,
	getCachedShiftRequestTypes,
} from 'core/utils/shiftRequestTypesCache';
import AvailabilityForm from './forms/AvailabilityForm';
import TimeOffForm from './forms/TimeOffForm';
import CoverRequestForm from './forms/CoverRequestForm';
import PickupShiftForm from './forms/PickupShiftForm';
import TradeShiftForm from './forms/TradeShiftForm';
import './EmployeeRequestModal.style.scss';

interface EmployeeRequestModalProps {
	closeModal: () => void;
	refetchSchedules?: () => void;
	currentWeekStart?: Date;
	currentWeekEnd?: Date;
	editRequest?: ShiftRequest | null;
	defaultRequestType?: RequestType;
	hideRequestTypeSelector?: boolean;
	userId?: number;
}

type RequestType = 'availability' | 'time-off' | 'cover' | 'pickup' | 'trade' | '';

const EmployeeRequestModal: React.FC<EmployeeRequestModalProps> = ({
	closeModal,
	refetchSchedules,
	currentWeekStart,
	currentWeekEnd,
	editRequest,
	defaultRequestType = '',
	hideRequestTypeSelector = false,
	userId,
}) => {
	const { workplaceId } = useWorkplacesContext();
	const { showToast } = useToastContext();
	const queryClient = useQueryClient();
	const [requestType, setRequestType] = useState<RequestType>(defaultRequestType);
	const [selectedTypeData, setSelectedTypeData] = useState<ShiftRequestType | null>(null);
	const [isWeekPublished, setIsWeekPublished] = useState(false);
	const [isCheckingPublished, setIsCheckingPublished] = useState(false);
	const formRef = useRef<any>(null);

	// Reset form when week changes (unless editing an existing request)
	React.useEffect(() => {
		if (!editRequest && formRef.current) {
			formRef.current.resetForm();
		}
	}, [currentWeekStart, currentWeekEnd, editRequest]);

	// Reset published status when request type changes
	React.useEffect(() => {
		setIsWeekPublished(false);
		setIsCheckingPublished(false);
	}, [requestType]);

	// Prefer cache (filled by SchedulePage); fetch when missing so Settings / other entry points still work
	const cachedTypes = React.useMemo(() => getCachedShiftRequestTypes(), []);
	const { data: fetchedTypesData } = useQuery(['shift-request-types'], getShiftRequestTypes, {
		enabled: !cachedTypes?.length,
		staleTime: Infinity,
		cacheTime: Infinity,
		onSuccess: (data) => {
			if (data?.member?.length) {
				cacheShiftRequestTypes(data.member);
			}
		},
		onError: (error: any) => {
			showToast('error', error?.message || 'Failed to fetch request types');
		},
	});

	const typesData = React.useMemo(() => {
		if (cachedTypes?.length) {
			return {
				member: cachedTypes,
				'@context': '',
				'@id': '',
				'@type': '',
				totalItems: cachedTypes.length,
			};
		}
		return fetchedTypesData || null;
	}, [cachedTypes, fetchedTypesData]);

	// Set up edit mode if editRequest is provided
	React.useEffect(() => {
		if (editRequest && typesData?.['member']) {
			const type = typesData['member'].find((t: ShiftRequestType) => 
				editRequest.type.id === t.id
			);
			if (type) {
				setSelectedTypeData(type);
				setRequestType(getFormTypeFromLabel(type.label));
			}
		}
	}, [editRequest, typesData]);

	// Set up selectedTypeData if defaultRequestType is provided
	React.useEffect(() => {
		if (defaultRequestType && typesData?.['member'] && !editRequest) {
			const type = typesData['member'].find((t: ShiftRequestType) => 
				getFormTypeFromLabel(t.label) === defaultRequestType
			);
			if (type) {
				setSelectedTypeData(type);
				setRequestType(defaultRequestType);
			}
		}
	}, [defaultRequestType, typesData, editRequest]);

	// Create mutation
	const { mutate: createMutation, isLoading: isCreating } = useMutation(
		createShiftRequest,
		{
			onSuccess: () => {
				queryClient.invalidateQueries(['shift-requests']);
				if (refetchSchedules) {
					refetchSchedules();
				}
				closeModal();
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to create shift request');
			},
		}
	);

	// Update mutation
	const { mutate: updateMutation, isLoading: isUpdating } = useMutation(
		({ id, payload }: { id: number; payload: UpdateShiftRequestInput }) =>
			updateShiftRequest(id, payload),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(['shift-requests']);
				if (refetchSchedules) {
					refetchSchedules();
				}
				closeModal();
			},
			onError: (error: any) => {
				showToast('error', error?.message || 'Failed to update shift request');
			},
		}
	);

	const handleSubmitClick = () => {
		if (formRef.current) {
			formRef.current.submitForm();
		}
	};

	const getFormTypeFromLabel = (label: string): RequestType => {
		const labelLower = label.toLowerCase();
		if (labelLower.includes('availability')) return 'availability';
		if (labelLower.includes('time off')) return 'time-off';
		if (labelLower.includes('cover')) return 'cover';
		if (labelLower.includes('pickup') || labelLower.includes('pick up')) return 'pickup';
		if (labelLower.includes('trade')) return 'trade';
		return '';
	};

	const handleSubmit = async (formValues: any) => {
		try {
			if (!selectedTypeData) {
				showToast('error', 'Please select a request type');
				return;
			}

			const formType = getFormTypeFromLabel(selectedTypeData.label);

			// Handle Availability requests (multiple requests, one per day)
			if (formType === 'availability') {
				const currentUserId = localStorage.getItem('userId');
				const requests = formValues.availabilityRequests || [];
				const typeIri = `/api/shift_request_types/${selectedTypeData.id}`;
				
				const promises = requests.map((req: any) => {
					if (req.requestId) {
						const payload: UpdateShiftRequestInput = {
							workplace: `api/workplaces/${workplaceId}`,
							type: typeIri,
							startAt: req.startTime || null,
							endAt: req.endTime || null,
							employeeRequest: currentUserId ? `api/users/${currentUserId}` : null,
							note: formValues.notes || null,
							day: req.day ? `api/days/${req.day}` : null,
						};
						return updateShiftRequest(req.requestId, payload);
					}

					const payload: CreateShiftRequestPayload = {
						requestType: 'availability',
						workplace: `api/workplaces/${workplaceId}`,
						type: typeIri,
						startAt: req.startTime || null,
						endAt: req.endTime || null,
						employeeRequest: currentUserId ? `api/users/${currentUserId}` : null,
						note: formValues.notes || null, // Mandatory for availability
						day: req.day ? `api/days/${req.day}` : null, // Mandatory for availability
					};
					return createShiftRequest(payload);
				});

				await Promise.all(promises);
				queryClient.invalidateQueries(['shift-requests']);
				queryClient.invalidateQueries(['user-availability']); // Invalidate user availability data
				if (refetchSchedules) {
					refetchSchedules();
				}
				closeModal();
				return;
			}

			// Handle Time Off requests
			if (formType === 'time-off') {
				const currentUserId = localStorage.getItem('userId');
				const typeIri = `/api/shift_request_types/${selectedTypeData.id}`;

				if (editRequest) {
					const payload: UpdateShiftRequestInput = {
						workplace: `api/workplaces/${workplaceId}`,
						type: typeIri,
						startAt: formValues.startAt || formValues.startDateTime || null,
						endAt: formValues.endAt || formValues.endDateTime || null,
						employeeRequest: currentUserId ? `api/users/${currentUserId}` : null,
						timeOffReasons: formValues.timeOffReasons || null,
						note: formValues.notes || null,
					};
					updateMutation({ id: editRequest.id, payload });
				} else {
					const payload: CreateShiftRequestPayload = {
						requestType: 'time-off',
						workplace: `api/workplaces/${workplaceId}`,
						type: typeIri,
						startAt: formValues.startAt || formValues.startDateTime || null,
						endAt: formValues.endAt || formValues.endDateTime || null,
						employeeRequest: currentUserId ? `api/users/${currentUserId}` : null,
						allDay: formValues.allDay !== false,
						timeOffReasons: formValues.timeOffReasons || null, // This is the timeoff reason label/IRI
						reasonDescription: formValues.reasonDescription || null, // Description from selected time off reason
						reasonNote: formValues.reasonNote || null, // Note from selected time off reason
						note: formValues.notes || null, // Employee's additional notes
					};
					createMutation(payload);
				}
				return;
			}

			// Handle Cover Request
			if (formType === 'cover') {
				const currentUserId = localStorage.getItem('userId');
				const typeIri = `/api/shift_request_types/${selectedTypeData.id}`;

				if (editRequest) {
					const payload: UpdateShiftRequestInput = {
						workplace: `api/workplaces/${workplaceId}`,
						type: typeIri,
						shiftRequest: formValues.shiftId ? `api/shifts/${formValues.shiftId}` : null,
						employeeRequest: currentUserId ? `api/users/${currentUserId}` : null,
						note: formValues.notes || null,
						timeOffReasons: formValues.reason || null,
					};
					updateMutation({ id: editRequest.id, payload });
				} else {
					const payload: CreateShiftRequestPayload = {
						requestType: 'cover',
						workplace: `api/workplaces/${workplaceId}`,
						type: typeIri,
						shiftRequest: formValues.shiftId ? `api/shifts/${formValues.shiftId}` : null, // Shift to be covered
						employeeRequest: currentUserId ? `api/users/${currentUserId}` : null,
						note: formValues.notes || null, // Free text note
						timeOffReasons: formValues.reason || null, // Time off reason IRI
						reasonDescription: formValues.reasonDescription || null, // Description from selected time off reason
						reasonNote: formValues.reasonNote || null,
					};
					createMutation(payload);
				}
				return;
			}

			// Handle Pickup Shift requests
			if (formType === 'pickup') {
				const currentUserId = localStorage.getItem('userId');
				const typeIri = `/api/shift_request_types/${selectedTypeData.id}`;

				if (editRequest) {
					const payload: UpdateShiftRequestInput = {
						workplace: `api/workplaces/${workplaceId}`,
						type: typeIri,
						shiftRequest: formValues.shiftId ? `api/shifts/${formValues.shiftId}` : null,
						employeeRequest: currentUserId ? `api/users/${currentUserId}` : null,
						note: formValues.notes || null,
					};
					updateMutation({ id: editRequest.id, payload });
				} else {
					const payload: CreateShiftRequestPayload = {
						requestType: 'pickup',
						workplace: `api/workplaces/${workplaceId}`,
						type: typeIri,
						shiftRequest: formValues.shiftId ? `api/shifts/${formValues.shiftId}` : null, // Shift to be picked up
						employeeRequest: currentUserId ? `api/users/${currentUserId}` : null, // Employee submitting the request
						note: formValues.notes || null,
					};
					createMutation(payload);
				}
				return;
			}

			// Handle Trade Shift requests
			if (formType === 'trade') {
				const currentUserId = localStorage.getItem('userId');
				const typeIri = `/api/shift_request_types/${selectedTypeData.id}`;

				if (editRequest) {
					const payload: UpdateShiftRequestInput = {
						workplace: `api/workplaces/${workplaceId}`,
						type: typeIri,
						startAt: null,
						endAt: null,
						shiftRequest: formValues.myShiftId ? `api/shifts/${formValues.myShiftId}` : null,
						employeeRequest: currentUserId ? `api/users/${currentUserId}` : null,
						employeeAssigned: formValues.tradeWithEmployeeId
							? `api/users/${formValues.tradeWithEmployeeId}`
							: null,
						shiftAssigned: formValues.theirShiftId ? `api/shifts/${formValues.theirShiftId}` : null,
						note: formValues.notes || null,
					};
					updateMutation({ id: editRequest.id, payload });
				} else {
					const payload: CreateShiftRequestPayload = {
						requestType: 'trade',
						workplace: `api/workplaces/${workplaceId}`,
						type: typeIri,
						startAt: null,
						endAt: null,
						shiftRequest: formValues.myShiftId ? `api/shifts/${formValues.myShiftId}` : null, // Current user's shift
						employeeRequest: currentUserId ? `api/users/${currentUserId}` : null, // Current user
						employeeAssigned: formValues.tradeWithEmployeeId
							? `api/users/${formValues.tradeWithEmployeeId}`
							: null, // User with the second shift
						shiftAssigned: formValues.theirShiftId ? `api/shifts/${formValues.theirShiftId}` : null, // Second shift to be traded
						note: formValues.notes || null,
					};
					createMutation(payload);
				}
				return;
			}

			showToast('error', 'Unknown request type');
		} catch (error: any) {
			showToast('error', error?.message || 'Failed to submit request');
		}
	};

	const handleTypeChange = (typeId: string) => {
		const type = typesData?.['member']?.find((t: ShiftRequestType) => t.id.toString() === typeId);
		if (type) {
			setSelectedTypeData(type);
			setRequestType(getFormTypeFromLabel(type.label));
		}
	};

	const renderForm = () => {
		switch (requestType) {
			case 'availability':
				return (
					<AvailabilityForm
						onSubmit={handleSubmit}
						onCancel={closeModal}
						formRef={formRef}
						userId={userId}
						editRequest={editRequest}
					/>
				);
			case 'time-off':
			return <TimeOffForm onSubmit={handleSubmit} onCancel={closeModal} formRef={formRef} onPublishedStatusChange={setIsWeekPublished} onCheckingStatusChange={setIsCheckingPublished} editRequest={editRequest} />;
			case 'cover':
				return (
					<CoverRequestForm
						onSubmit={handleSubmit}
						onCancel={closeModal}
						currentWeekStart={currentWeekStart}
						currentWeekEnd={currentWeekEnd}
						formRef={formRef}
						editRequest={editRequest}
					/>
				);
			case 'pickup':
				return (
					<PickupShiftForm
						onSubmit={handleSubmit}
						onCancel={closeModal}
						currentWeekStart={currentWeekStart}
						currentWeekEnd={currentWeekEnd}
						formRef={formRef}
						editRequest={editRequest}
					/>
				);
			case 'trade':
				return (
					<TradeShiftForm
						onSubmit={handleSubmit}
						onCancel={closeModal}
						currentWeekStart={currentWeekStart}
						currentWeekEnd={currentWeekEnd}
						formRef={formRef}
					/>
				);
			default:
				return null;
		}
	};

	const types = typesData?.['member'] || [];
	const isSaving = isCreating || isUpdating;

	return (
		<Paper sx={{ minHeight: '100vh', py: 3, borderRadius: 3, boxShadow: hideRequestTypeSelector ? 0 : 2 }}>
			<Box maxWidth="1100px" mx="auto" px={3}>
				{/* Header */}
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Button
						onClick={closeModal}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>
					<Typography variant="h5" sx={{ fontWeight: 600 }}>
						{editRequest ? 'Edit Request' : hideRequestTypeSelector ? 'Availability Request' : 'New Request'}
					</Typography>
					{requestType && (
						<Button
							onClick={handleSubmitClick}
						disabled={isSaving || (requestType === 'time-off' && (isWeekPublished || isCheckingPublished))}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								color: '#fff',
								textTransform: 'none',
								fontWeight: 500,
								px: 3,
								py: 0.75,
								'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
							}}
						>
							{isSaving ? 'Submitting...' : 'Submit'}
						</Button>
					)}
					{!requestType && <Box sx={{ width: '120px' }} />}
				</Box>

				{/* Request Form */}
				<Box sx={{ p: 4 }}>
					{!hideRequestTypeSelector && (
						<FormControl fullWidth sx={{ mb: 3 }}>
							<InputLabel>Request Type</InputLabel>
							<Select
								value={selectedTypeData?.id.toString() || ''}
								onChange={(e) => handleTypeChange(e.target.value)}
								label="Request Type"
								disabled={!!editRequest}
							>
								<MenuItem value="">Select a request type</MenuItem>
								{types
									.filter((type: ShiftRequestType) => !type.label.toLowerCase().includes('trade'))
									.map((type: ShiftRequestType) => (
										<MenuItem key={type.id} value={type.id.toString()}>
											{type.label}
										</MenuItem>
									))
								}
							</Select>
						</FormControl>
					)}

					{renderForm()}
				</Box>
			</Box>
		</Paper>
	);
};

export default EmployeeRequestModal;
